// Budget entry arithmetic belongs to terminal pots. Groups only total children.
// All helpers are immutable and use integer cents for arithmetic.
const clone = value => {
  if (Array.isArray(value)) return value.map(clone);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
  return value;
};
const metadataFor = value => {
  if (value == null) return {};
  const parsed = typeof value === 'string' ? JSON.parse(value) : value;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Budget pot settings are invalid.');
  return clone(parsed);
};
const parentKey = value => value == null || value === '' ? null : String(value);
const number = value => value === '' || value == null ? 0 : Number(value);
function cents(value) {
  const amount = number(value);
  const result = Math.round(amount * 100);
  if (!Number.isFinite(amount) || amount < 0 || amount >= 1e12 || !Number.isSafeInteger(result) || Math.abs(amount * 100 - result) > 0.0001) {
    throw new Error('Enter an allocation of zero or more, with at most two decimal places.');
  }
  return result;
}
function indexRows(rows) {
  const byId = new Map();
  const children = new Map();
  for (const row of rows) {
    if (!row.id) throw new Error('Every budget pot needs an identity.');
    if (byId.has(row.id)) throw new Error(`Duplicate budget pot identity: ${row.id}.`);
    byId.set(row.id, row);
    children.set(row.id, []);
  }
  for (const row of rows) {
    if (row.parentId != null) {
      if (!byId.has(row.parentId)) throw new Error(`The parent of ${row.name || row.id} is missing.`);
      children.get(row.parentId).push(row);
    }
  }
  const visited = new Set();
  const visiting = new Set();
  function visit(row) {
    if (visiting.has(row.id)) throw new Error('The budget hierarchy contains a cycle.');
    if (visited.has(row.id)) return;
    visiting.add(row.id);
    if (row.parentId != null) visit(byId.get(row.parentId));
    visiting.delete(row.id);
    visited.add(row.id);
  }
  rows.forEach(visit);
  return {
    byId,
    children
  };
}
function normalizeRows(pots, fiscalYear) {
  if (!Array.isArray(pots)) throw new Error('Budget pots must be a list.');
  const rows = pots.map(pot => {
    if (!pot || typeof pot !== 'object' || Array.isArray(pot)) throw new Error('A budget pot is invalid.');
    if (Array.isArray(pot.children) && pot.children.length) throw new Error('Use the complete flat budget list, not nested display rows.');
    const metadata = metadataFor(pot.metadata);
    const row = Object.assign({}, clone(pot), {
      id: pot.id == null ? '' : String(pot.id),
      parentId: parentKey(pot.parentId),
      name: String(pot.name ?? ''),
      code: String(pot.code ?? ''),
      approved: number(pot.approved),
      adjusted: number(pot.adjusted ?? pot.approved),
      fiscalYear: fiscalYear ?? pot.fiscalYear ?? null,
      metadata,
      planningGroup: pot.planningGroup === true || metadata.planningGroup === true || String(pot.nodeType || metadata.nodeType || '').trim().toLowerCase() === 'group'
    });
    // Nested display values are rebuilt from flat identities, never saved as data.
    delete row.children;
    delete row.allocation;
    delete row.allocationCents;
    delete row.isGroup;
    return row;
  });
  const {
    children
  } = indexRows(rows);
  return rows.map(row => {
    const planningGroup = row.planningGroup || children.get(row.id).length > 0;
    return Object.assign({}, row, {
      planningGroup,
      metadata: Object.assign({}, row.metadata, {
        planningGroup
      })
    });
  });
}
function buildTree(pots) {
  const rows = normalizeRows(pots);
  const {
    children
  } = indexRows(rows);
  function build(row) {
    const nested = children.get(row.id).map(build);
    const allocationCents = row.planningGroup ? nested.reduce((total, item) => total + item.allocationCents, 0) : cents(row.adjusted);
    if (!Number.isSafeInteger(allocationCents)) throw new Error('The total allocation is too large.');
    return Object.assign({}, row, {
      children: nested,
      isGroup: row.planningGroup,
      allocation: allocationCents / 100,
      allocationCents
    });
  }
  return rows.filter(row => row.parentId == null).map(build);
}
function getTotal(rows) {
  const total = buildTree(rows).reduce((sum, row) => sum + row.allocationCents, 0);
  if (!Number.isSafeInteger(total)) throw new Error('The total allocation is too large.');
  return total;
}
function validateRows(pots) {
  let rows;
  try {
    rows = normalizeRows(pots);
  } catch (error) {
    return [error.message];
  }
  const errors = [];
  const codes = new Set();
  for (const row of rows) {
    const name = row.name.trim();
    const code = row.code.trim();
    if (!name || name.length > 255) errors.push(`Enter a name of 1–255 characters for ${code || 'each budget pot'}.`);
    if (!code) errors.push(`Enter a code for ${name || 'each budget pot'}.`);
    if (code.length > 64) errors.push(`Keep the code for ${name || 'each budget pot'} to 64 characters or fewer.`);
    if (code && codes.has(code.toLowerCase())) errors.push(`The budget code ${code} is used more than once.`);
    codes.add(code.toLowerCase());
    if (!row.planningGroup) {
      try {
        cents(row.adjusted);
        cents(row.approved);
      } catch (error) {
        errors.push(`${name || code || 'Budget pot'}: ${error.message}`);
      }
    }
  }
  if (!errors.length) {
    try {
      getTotal(rows);
    } catch (error) {
      errors.push(error.message);
    }
  }
  return errors;
}
const calculatedFields = ['committed', 'actual', 'forecast', 'remaining', 'forecastVariance', 'pacing', 'adminShare', 'allocation', 'allocationCents', 'budgetRemainingPercent', 'budgetUsedPercent', 'monthlyCommitted', 'monthlyActual', 'monthlyProjected', 'projection', 'salaryCommitments', 'expenseCommitments', 'adjustments', 'approvals'];
function copyRows(pots, targetYear, {
  includeAmounts = true
} = {}) {
  if (!targetYear) throw new Error('Choose a destination fiscal year.');
  const rows = toDraftPots(pots);
  return normalizeRows(rows.map(row => {
    const next = Object.assign({}, row, {
      fiscalYear: targetYear,
      fiscalYearTag: targetYear,
      name: row.metadata.fiscalBudget?.displayName || row.name,
      code: row.code
    });
    // Unknown settings are retained. Only year-specific figures and change history
    // are removed; fiscalBudget.familyKey continues to identify the same fund.
    if (row.fiscalYear !== targetYear) {
      calculatedFields.forEach(key => {
        delete next[key];
        delete next.metadata[key];
      });
      delete next.evidence;
      delete next.metadata.evidence;
      if (next.metadata.fiscalBudget) {
        delete next.metadata.fiscalBudget.createdFromPotId;
        delete next.metadata.fiscalBudget.capStatus;
      }
      next.status = 'draft';
    }
    const amount = includeAmounts ? cents(row.adjusted) / 100 : 0;
    next.approved = row.fiscalYear === targetYear && includeAmounts ? row.approved : amount;
    next.adjusted = amount;
    return next;
  }), targetYear);
}
function requireRow(rows, id) {
  const row = rows.find(item => item.id === String(id));
  if (!row) throw new Error('Select a budget pot.');
  return row;
}
function requireNewId(rows, id) {
  if (id == null || String(id) === '') throw new Error('A new budget pot needs an identity.');
  if (rows.some(row => row.id === String(id))) throw new Error('That budget pot identity is already in use.');
  return String(id);
}
function groupCode(rows, selected, catalog = []) {
  const reference = selected.metadata?.fiscalBudget?.displayCode || selected.code || selected.name;
  // Keep the human code short; the existing id/familyKey carries identity.
  // Compact the reference so legacy SAL/OPS/TRV token parsing is not introduced.
  const compact = String(reference).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/(?:19|20)\d{2}/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  const base = compact ? `GRP-${compact}` : 'GRP';
  const used = new Set([...rows, ...catalog].flatMap(item => [item.code, metadataFor(item.metadata).fiscalBudget?.displayCode]).map(code => String(code || '').toUpperCase()));
  let candidate = base;
  for (let suffix = 2; used.has(candidate); suffix += 1) {
    const ending = `-${suffix}`;
    candidate = `${base.slice(0, 16 - ending.length)}${ending}`;
  }
  return candidate;
}
function repairDraftGroupCodes(pots, catalog = []) {
  const rows = normalizeRows(pots);
  for (const row of rows) {
    const family = row.metadata.fiscalBudget?.familyKey;
    const published = catalog.some(pot => String(pot.id) === row.id || (family && metadataFor(pot.metadata).fiscalBudget?.familyKey === family));
    if (published || !row.planningGroup || family !== `planning-group:${row.id}` || !/^GROUP-(?:[A-F0-9]{8}|[A-F0-9]{24})(?:-\d+)?$/i.test(row.code)) continue;
    const child = rows.find(item => item.parentId === row.id);
    row.code = groupCode(rows, child || Object.assign({}, row, {
      code: '',
      metadata: {}
    }), catalog);
    row.metadata.fiscalBudget = Object.assign({}, row.metadata.fiscalBudget, {
      displayCode: row.code
    });
  }
  return rows;
}
function restoreEmptyDraftParents(pots, catalog = [], formerParentIds = []) {
  const rows = normalizeRows(pots);
  const parents = new Set(rows.map(row => row.parentId));
  return normalizeRows(rows.map(row => {
    if (!row.planningGroup || parents.has(row.id)) return row;
    const family = row.metadata.fiscalBudget?.familyKey;
    const original = catalog.find(pot => String(pot.id) === row.id || (family && metadataFor(pot.metadata).fiscalBudget?.familyKey === family));
    const originalMetadata = metadataFor(original?.metadata);
    const originalType = original?.nodeType || originalMetadata.nodeType || 'budget';
    const originalLeaf = original && !original.planningGroup && !originalMetadata.planningGroup && String(originalType).toLowerCase() !== 'group' && !catalog.some(pot => String(pot.parentId) === String(original.id));
    if (!formerParentIds.includes(row.id) && !row.metadata.planningLeafType && !originalLeaf) return row;
    const nodeType = row.metadata.planningLeafType || (originalLeaf ? originalType : String(row.nodeType || '').toLowerCase() !== 'group' && row.nodeType) || 'budget';
    const metadata = Object.assign({}, row.metadata, {
      nodeType,
      planningGroup: false
    });
    delete metadata.planningLeafType;
    return Object.assign({}, row, {
      nodeType,
      planningGroup: false,
      approved: 0,
      adjusted: 0,
      metadata
    });
  }));
}
function addChild(pots, selectedId, child, newChildId, catalog = []) {
  const rows = normalizeRows(pots);
  const selected = requireRow(rows, selectedId);
  if (selected.planningGroup) {
    const id = requireNewId(rows, child.id);
    const added = Object.assign({}, clone(child), {
      id,
      parentId: selected.id,
      fiscalYear: selected.fiscalYear,
      fiscalYearTag: selected.fiscalYear,
      approved: 0,
      adjusted: 0,
      planningGroup: false,
      metadata: Object.assign({}, metadataFor(child.metadata), {
        planningGroup: false
      })
    });
    return normalizeRows([...rows, added]);
  }
  const id = requireNewId(rows, child.id || newChildId);
  const name = String(child.name ?? selected.name).trim();
  const used = new Set([...rows, ...catalog].flatMap(item => [item.code, metadataFor(item.metadata).fiscalBudget?.displayCode]).map(value => String(value || '').trim().toUpperCase()));
  let code = String(child.code || '').trim();
  if (!code) {
    const base = String(selected.metadata.fiscalBudget?.displayCode || selected.code || 'POT').slice(0, 55);
    let suffix = 1;
    do {
      code = `${base}-${suffix++}`;
    } while (used.has(code.toUpperCase()));
  }
  if (code.length > 64 || /^\d+$/.test(code)) throw new Error('Enter a budget code of 1–64 characters including a non-numeric character.');
  if (used.has(code.toUpperCase())) throw new Error('This code is already used by another pot.');
  // The parent keeps its identity, code, position and history. This is a draft
  // structure change: published-family dependencies must be reviewed before publication.
  const group = Object.assign({}, selected, {
    nodeType: 'group',
    planningGroup: true,
    approved: 0,
    adjusted: 0,
    metadata: Object.assign({}, selected.metadata, {
      nodeType: 'group',
      planningGroup: true,
      planningLeafType: selected.nodeType || selected.metadata.nodeType || 'budget'
    })
  });
  const added = Object.assign({}, clone(selected), {
    id,
    parentId: selected.id,
    name,
    code,
    planningGroup: false,
    metadata: Object.assign({}, clone(selected.metadata), {
      planningGroup: false,
      fiscalBudget: {
        version: 1,
        familyKey: `planning-pot:${id}`,
        displayName: name,
        displayCode: code
      }
    })
  });
  // Copy settings and allocation, not recorded commitments or adjustment history.
  for (const key of [...calculatedFields, 'evidence']) {
    delete added[key];
    delete added.metadata[key];
  }
  return normalizeRows(rows.flatMap(row => row.id === selected.id ? [group, added] : [row]));
}
function getDescendantIds(pots, selectedId) {
  const rows = normalizeRows(pots);
  const selected = requireRow(rows, selectedId);
  const {
    children
  } = indexRows(rows);
  const result = [];
  function visit(row) {
    children.get(row.id).forEach(child => {
      result.push(child.id);
      visit(child);
    });
  }
  visit(selected);
  return result;
}
function moveRow(pots, id, newParentId, catalog = []) {
  const rows = normalizeRows(pots);
  const selected = requireRow(rows, id);
  const parentId = parentKey(newParentId);
  if (parentId === selected.id || getDescendantIds(rows, selected.id).includes(parentId)) {
    throw new Error('A pot cannot be moved inside itself or one of its children.');
  }
  if (parentId != null && !requireRow(rows, parentId).planningGroup) {
    throw new Error('Choose a parent group or the top level.');
  }
  return restoreEmptyDraftParents(rows.map(row => row.id === selected.id ? Object.assign({}, row, {
    parentId
  }) : row), catalog, [selected.parentId]);
}
function removeRow(pots, id, catalog = []) {
  const rows = normalizeRows(pots);
  const selected = requireRow(rows, id);
  const removed = new Set([selected.id, ...getDescendantIds(rows, selected.id)]);
  return restoreEmptyDraftParents(rows.filter(row => !removed.has(row.id)), catalog, [selected.parentId]);
}
function setAllocation(pots, id, value) {
  const rows = normalizeRows(pots);
  const selected = requireRow(rows, id);
  if (selected.planningGroup) throw new Error('A parent allocation is calculated from its children.');
  const amount = cents(value) / 100;
  return rows.map(row => row.id === selected.id ? Object.assign({}, row, {
    adjusted: amount
  }) : row);
}
function toDraftPots(pots) {
  const rows = normalizeRows(pots);
  const {
    children
  } = indexRows(rows);
  const totals = new Map();
  function calculate(row) {
    const amounts = row.planningGroup ? children.get(row.id).map(calculate).reduce((sum, item) => ({
      approved: sum.approved + item.approved,
      adjusted: sum.adjusted + item.adjusted
    }), {
      approved: 0,
      adjusted: 0
    }) : {
      approved: cents(row.approved),
      adjusted: cents(row.adjusted)
    };
    if (!Number.isSafeInteger(amounts.approved) || !Number.isSafeInteger(amounts.adjusted)) throw new Error('The total allocation is too large.');
    totals.set(row.id, amounts);
    return amounts;
  }
  rows.filter(row => row.parentId == null).forEach(calculate);
  return rows.map(row => Object.assign({}, row, {
    approved: totals.get(row.id).approved / 100,
    adjusted: totals.get(row.id).adjusted / 100
  }));
}
module.exports = {
  normalizeRows,
  buildTree,
  getTotal,
  validateRows,
  copyRows,
  repairDraftGroupCodes,
  restoreEmptyDraftParents,
  addChild,
  getDescendantIds,
  moveRow,
  removeRow,
  setAllocation,
  toDraftPots
};
